import React from 'react'
import styled from 'styled-components'
import moment from 'moment'
import qs from 'qs'
import { rem } from 'polished'
import { FlightDeal } from 'components/Flights/types'
import { fareTypes, fareCabins } from 'constants/flight'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'

import { getFlightDealDuration } from 'lib/flights'

import Caption from 'components/Luxkit/Typography/Caption'
import BodyText from 'components/Luxkit/Typography/BodyText'
import InlineGroup from 'components/Common/InlineGroup'
import SolidExchangeAltIcon from 'components/Luxkit/Icons/solid/SolidExchangeAltIcon'
import Spacing from 'components/Common/Spacing'
import Hr from 'components/Common/Hr'
import TextButton from 'components/Luxkit/Button/TextButton'
import Heading from 'components/Luxkit/Typography/Heading'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import PromotionLabel from 'components/Luxkit/Label/PromotionLabel'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import LineAwardIcon from 'components/Luxkit/Icons/line/LineAwardIcon'

const Root = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.five};
  border-radius: ${props => props.theme.borderRadius.large};
  padding: ${rem(16)};
`

const Block = styled(InlineGroup)`
  flex-direction: column;
`

const Header = styled(InlineGroup)`
  height: ${rem(52)};
`

const StyledHr = styled(Hr)`
  border-color: ${props => props.theme.palette.neutral.six};
`

const AirlineLogo = styled.img`
  width: ${rem(100)};
`

const FlightDuration = styled(Caption)`
  height: ${rem(36)};
  white-space: pre-wrap;
`

interface Props {
  flightDeal: FlightDeal;
}

function FlightDealTile({ flightDeal }: Props) {
  const {
    id,
    originAirportCode,
    originAirportName,
    destinationAirportCode,
    destinationAirportName,
    fareType,
    fareClass,
    perAdultPrice,
    salesEndDate,
    offerDuration,
    carrierLogo,
    carrierCode,
    adultCount,
    discountLabel,
  } = flightDeal

  const fareTypeLabel = fareTypes.find(type => type.value === fareType)?.label
  const fareClassLabel = fareCabins.find(type => type.value === fareClass)?.label
  const salesEndingDate = moment(salesEndDate).format(DMY_CASUAL_FORMAT)
  const offerDurationLabel = getFlightDealDuration(offerDuration)
  const isOnewayFlight = fareType === 'oneWay'

  const viewFlightsURL = '/flights?' + qs.stringify({
    origin: originAirportCode,
    destination: destinationAirportCode,
    adults: adultCount,
    flightDeal: id,
    fareClass,
    fareType,
  })

  return (
    <Root>
      <Block>
        <Header horizontal="between" vertical="center">
          <AirlineLogo
            src={carrierLogo}
            alt={`Logo of ${carrierCode}`}
          />
          <Block vertical="end">
            <UrgencyLabel variant="positive">Sale ends {salesEndingDate}</UrgencyLabel>
            <Spacing mt={4}>
              <PromotionLabel variant="default" icon={<LineAwardIcon />}>{discountLabel}</PromotionLabel>
            </Spacing>
          </Block>
        </Header>
        <Spacing mt={16}>
          <InlineGroup vertical="end" spacing={isOnewayFlight ? 'sm' : 'md'}>
            <Block>
              <Caption variant="medium" colour="neutral-three">{originAirportCode}</Caption>
              <BodyText variant="large" weight="bold">{originAirportName}</BodyText>
            </Block>
            {isOnewayFlight ? <LineArrowRightIcon size="M" /> : <SolidExchangeAltIcon size="M" />}
            <Block>
              <Caption variant="medium" colour="neutral-three">{destinationAirportCode}</Caption>
              <BodyText variant="large" weight="bold">{destinationAirportName}</BodyText>
            </Block>
          </InlineGroup>
        </Spacing>
        <Spacing mt={8}>
          <Block>
            <Caption variant="medium" colour="neutral-three">
              {fareTypeLabel} flights • {fareClassLabel}
            </Caption>
            <FlightDuration variant="medium" colour="neutral-three">
              Travel between {offerDurationLabel}
            </FlightDuration>
          </Block>
        </Spacing>
      </Block>
      <StyledHr size="xs" />
      <InlineGroup horizontal="between" vertical="center">
        <Block>
          <Caption variant="medium" colour="neutral-three">
            {fareTypeLabel} from
          </Caption>
          <InlineGroup vertical="center">
            <Heading variant="heading6">
              <FormatCurrency value={Number(perAdultPrice)} />
            </Heading>
            <Spacing ml={4}>
              <Caption variant="medium">
                /adult
              </Caption>
            </Spacing>
          </InlineGroup>
        </Block>
        <TextButton kind="tertiary" to={viewFlightsURL} target="_blank">
          View flights
        </TextButton>
      </InlineGroup>
    </Root>
  )
}

export default FlightDealTile
